import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve a stored asset path/URL to a renderable src.
// Handles: (a) full http(s) URLs, (b) base64 data URIs (Quick Post photos),
// (c) backend-relative paths like "/uploads/abc.jpg". Returns empty string
// for null/undefined so the <img> just shows the alt text.
export function resolveAssetUrl(url) {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    return url;
  }
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// ============ Auto-refresh interceptor ============
// On 401 from any authenticated request, try /api/auth/refresh once, then retry.
// Attach to BOTH the `api` custom instance AND the default `axios` (AuthContext uses raw axios).
let refreshInFlight = null;
const AUTH_SKIP_URLS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/register'];

function shouldSkipRefresh(config) {
  if (!config || !config.url) return true;
  return AUTH_SKIP_URLS.some(u => config.url.includes(u));
}

function installAuthRefresh(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;
      if (status !== 401 || !original || original._retried || shouldSkipRefresh(original)) {
        return Promise.reject(error);
      }
      original._retried = true;
      try {
        // De-duplicate concurrent refreshes
        if (!refreshInFlight) {
          refreshInFlight = axios.post(
            `${API_URL}/api/auth/refresh`,
            {},
            { withCredentials: true }
          ).finally(() => {
            // Allow next refresh attempt after this one resolves
            setTimeout(() => { refreshInFlight = null; }, 0);
          });
        }
        await refreshInFlight;
        // Retry the original request with the new access cookie
        return instance.request(original);
      } catch (refreshErr) {
        // Refresh failed — user is truly logged out. Redirect to login for explicit UX
        // (skip if we're already on a public/auth page to avoid redirect loops).
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/terms', '/privacy', '/for-customers', '/for-drivers', '/for-businesses', '/install', '/app', '/download', '/quick-post', '/drivers'];
        const isPublic = publicPaths.some(p => path === p || path.startsWith(p + '/'));
        if (typeof window !== 'undefined' && !isPublic) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
  );
}

installAuthRefresh(api);
installAuthRefresh(axios);

// Delivery APIs
export const createDelivery = (data) => api.post('/deliveries', data);
export const uploadDeliveryPhotos = (deliveryId, formData) => api.post(`/deliveries/${deliveryId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getMyDeliveries = () => api.get('/deliveries/my');
export const getPaginatedDeliveries = (params) => api.get('/deliveries/paginated', { params });
export const getHistorySummary = () => api.get('/deliveries/history-summary');
export const getAvailableDeliveries = (params) => api.get('/deliveries/available', { params });
export const getRecentCompletedDeliveries = () => api.get('/deliveries/recent-completed');
export const getAllDeliveries = () => api.get('/deliveries/all');
export const deleteDelivery = (deliveryId) => api.delete(`/admin/deliveries/${deliveryId}`);
export const bulkDeleteDeliveries = (deliveryIds) => api.post('/admin/deliveries/bulk-delete', { delivery_ids: deliveryIds });
export const getDelivery = (id) => api.get(`/deliveries/${id}`);
export const updateDeliveryStatus = (id, status) => api.put(`/deliveries/${id}/status`, { status });
export const driverOnTheWay = (deliveryId, data) => api.put(`/deliveries/${deliveryId}/on-the-way`, data);
export const verifyDelivery = (deliveryId, formData) => api.post(`/deliveries/${deliveryId}/verify-delivery`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getCounties = () => api.get('/counties');

// Offer APIs
export const createOffer = (deliveryId, data) => api.post(`/deliveries/${deliveryId}/offers`, data);
export const askPreBidQuestion = (deliveryId, text) => api.post(`/deliveries/${deliveryId}/question`, { text });
export const getDriverVehicle = () => api.get('/driver/vehicle');
export const updateDriverVehicle = (data) => api.put('/driver/vehicle', data);
export const getOffers = (deliveryId) => api.get(`/deliveries/${deliveryId}/offers`);
export const getMyOffers = () => api.get('/offers/my');
export const acceptAndPay = (deliveryId, offerId, paymentMethod = 'stripe') => api.post(`/deliveries/${deliveryId}/accept-and-pay`, { offer_id: offerId, payment_method: paymentMethod });

// Admin APIs
export const assignDriver = (deliveryId, offerId) => api.post(`/admin/deliveries/${deliveryId}/assign`, { offer_id: offerId });
export const getAdminStats = () => api.get('/admin/stats');
export const getAllUsers = () => api.get('/admin/users');
export const getRevenueStats = (period = 'all_time') => api.get(`/admin/revenue?period=${period}`);
export const processPayouts = () => api.post('/admin/process-payouts');
export const editUser = (userId, data) => api.put(`/admin/users/${userId}`, data);
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const bulkDeleteUsers = (userIds) => api.post('/admin/users/bulk-delete', { user_ids: userIds });
export const toggleBanUser = (userId) => api.put(`/admin/users/${userId}/ban`);

// Payment APIs
export const getPaymentsConfig = () => api.get('/payments/config');
export const createPaymentCheckout = (deliveryId, originUrl) => api.post('/payments/checkout', { delivery_id: deliveryId, origin_url: originUrl });
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getDeliveryPayment = (deliveryId) => api.get(`/payments/delivery/${deliveryId}`);

// Stripe Connect APIs (for drivers)
export const createConnectOnboarding = (originUrl) => api.post('/connect/onboard', { origin_url: originUrl });
export const getConnectStatus = () => api.get('/connect/status');
export const getConnectDashboard = () => api.get('/connect/dashboard');
export const getDriverEarnings = (params) => api.get('/driver/earnings', { params });

// Notification APIs
export const getNotifications = () => api.get('/notifications');
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Auth APIs
export const verifyEmail = (token) => api.post(`/auth/verify-email/${token}`);
export const resendVerification = (email) => api.post('/auth/resend-verification', { email });
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Driver Profile APIs
export const uploadProfilePhoto = (formData) => api.post('/driver/profile-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getDriverProfile = () => api.get('/driver/profile');
export const updateDriverBio = (bio) => api.put('/driver/bio', { bio });
export const getMessageStarters = () => api.get('/driver/message-starters');
export const saveMessageStarters = (starters) => api.put('/driver/message-starters', { starters });
export const updateDriverPaymentPrefs = (prefs) => api.put('/driver/payment-preferences', prefs);
export const updateDriverUseMode = (mode) => api.put('/driver/use-mode', { driver_use_mode: mode });
export const getMyReviews = () => api.get('/driver/my-reviews');
export const getAdminDriverDetails = (driverId) => api.get(`/admin/driver/${driverId}/details`);
export const getDriversByCity = () => api.get('/admin/drivers-by-city');
export const getDriversByCounty = () => api.get('/admin/drivers-by-county');
export const updateDriverCounties = (data) => api.put('/driver/counties', Array.isArray(data) ? { counties: data } : data);
export const getDriverComplianceStatus = () => api.get('/driver/compliance/status');
export const submitDriverCompliance = (data) => api.post('/driver/compliance', data);
export const listPendingReviewDrivers = () => api.get('/admin/drivers/pending-review');
export const reviewDriverCompliance = (driverId, decision, note) => api.put(`/admin/drivers/${driverId}/compliance`, { decision, note });
export const getDriverLicenceFile = (driverId) => api.get(`/admin/drivers/${driverId}/licence`);
export const toggleDriverPremium = (driverId) => api.put(`/admin/users/${driverId}/premium`);
export const getAdminUserNotes = (userId) => api.get(`/admin/users/${userId}/notes`);
export const addAdminUserNote = (userId, text) => api.post(`/admin/users/${userId}/notes`, { text });
export const deleteAdminUserNote = (userId, noteId) => api.delete(`/admin/users/${userId}/notes/${noteId}`);

// Driver Insurance Declaration (tickbox only — no certificate upload)
export const getDriverInsurance = () => api.get('/driver/insurance');
export const acceptInsuranceDeclaration = () => api.post('/driver/insurance/declaration');
export const adminListInsurance = (search = '') => api.get(`/admin/insurance${search ? `?search=${encodeURIComponent(search)}` : ''}`);

// Support chat (user ↔ admin only)
export const supportGetMyThread = () => api.get('/support/messages');
export const supportSendMessage = (body) => api.post('/support/messages', { body });
export const supportUnreadCount = () => api.get('/support/unread-count');
export const supportAdminThreads = () => api.get('/support/admin/threads');
export const supportAdminThread = (userId) => api.get(`/support/admin/threads/${userId}`);
export const supportAdminReply = (userId, body) => api.post(`/support/admin/threads/${userId}/reply`, { body });

// Admin Activity Log
export const getAdminAuditLog = ({ action = '', admin_id = '', target_id = '', limit = 100, skip = 0 } = {}) =>
  api.get('/admin/audit-log', { params: { action, admin_id, target_id, limit, skip } });

// Manual capture lifecycle
export const cancelAuthorizedPayment = (deliveryId) => api.post(`/deliveries/${deliveryId}/cancel-payment`);
export const refundDelivery = (deliveryId, { amount_eur, reason } = {}) => api.post(`/admin/deliveries/${deliveryId}/refund`, { amount_eur, reason });
export const listDueCaptures = () => api.get('/admin/captures/due');
export const adminForceCapture = (deliveryId) => api.post(`/admin/deliveries/${deliveryId}/force-capture`);
export const resendReceipt = (deliveryId) => api.post(`/admin/deliveries/${deliveryId}/resend-receipt`);
export const nudgeCustomer = (deliveryId) => api.post(`/admin/deliveries/${deliveryId}/nudge-customer`);

// Direct payment confirmation (record-keeping only)
export const getDriverPaymentMethods = () => api.get('/driver/payment-methods');
export const customerCancelBooking = (deliveryId, payload) => api.post(`/deliveries/${deliveryId}/cancel-booking`, payload);
export const driverCancelBooking = (deliveryId, payload) => api.post(`/deliveries/${deliveryId}/driver-cancel`, payload);
export const getCancellationPreview = (deliveryId) => api.get(`/admin/deliveries/${deliveryId}/cancellation-preview`);
export const cancelWithCompensation = (deliveryId, payload) => api.post(`/admin/deliveries/${deliveryId}/cancel-with-compensation`, payload);
export const getOwedCompensations = () => api.get('/admin/compensations/owed');
export const retryCompensationTransfer = (deliveryId) => api.post(`/admin/compensations/${deliveryId}/retry-transfer`);
export const downloadVatInvoiceUrl = (deliveryId) => `${API_URL}/api/deliveries/${deliveryId}/vat-invoice.pdf`;
export const driverEarningsStatementUrl = (year, month) => `${API_URL}/api/driver/earnings/statement.pdf?year=${year}&month=${month}`;
export const driverEarningsStatementRangeUrl = (fromDate, toDate) => `${API_URL}/api/driver/earnings/statement.pdf?from=${fromDate}&to=${toDate}`;
export const emailReceipt = (deliveryId) => api.post(`/deliveries/${deliveryId}/email-receipt`);
export const editDelivery = (deliveryId, patch) => api.patch(`/deliveries/${deliveryId}`, patch);
export const cancelDelivery = (deliveryId, { reason, reason_label } = {}) =>
  api.delete(`/deliveries/${deliveryId}`, {
    params: { ...(reason ? { reason } : {}), ...(reason_label ? { reason_label } : {}) },
  });
export const editOffer = (offerId, patch) => api.patch(`/offers/${offerId}`, patch);
export const withdrawOffer = (offerId) => api.delete(`/offers/${offerId}`);

// Ask-a-Question thread APIs
export const getOfferThread = (offerId) => api.get(`/offers/${offerId}/thread`);
export const postOfferReply = (offerId, text) => api.post(`/offers/${offerId}/reply`, { text });
export const markDeliveryMessagesRead = (deliveryId) => api.post(`/deliveries/${deliveryId}/messages/read`);
export const adminListThreads = (q) => api.get('/admin/threads', { params: q ? { q } : {} });

// Messages Inbox APIs (central messaging hub).
export const listConversations = ({ archived = 0, limit = 200 } = {}) =>
  api.get('/messages/conversations', { params: { archived, limit } });
export const getMessagesUnreadCount = () => api.get('/messages/unread-count');
export const markOfferThreadRead = (offerId) => api.post(`/offers/${offerId}/thread/read`);

// Review APIs
export const submitReview = (deliveryId, data) => api.post(`/deliveries/${deliveryId}/review`, data);
export const getDeliveryReview = (deliveryId) => api.get(`/deliveries/${deliveryId}/review`);
export const getDriverReviews = (driverId) => api.get(`/driver/${driverId}/reviews`);

// Verify a single intermediate drop on a multi-drop delivery (driver only).
export const verifyDrop = (deliveryId, dropIndex, code) =>
  api.post(`/deliveries/${deliveryId}/verify-drop`, { drop_index: dropIndex, code });

// ============ Driver Public Directory ============
export const listDirectoryDrivers = (params = {}) => api.get('/directory/drivers', { params });
export const getDirectoryDriver = (slug) => api.get(`/directory/drivers/${slug}`);
export const contactDirectoryDriver = (slug, payload) => api.post(`/directory/drivers/${slug}/contact`, payload);
export const submitDirectoryReview = (slug, payload) => api.post(`/directory/drivers/${slug}/review`, payload);
export const listDirectoryReviews = (slug, params = {}) => api.get(`/directory/drivers/${slug}/reviews`, { params });
export const directoryDriverPhotoUrl = (slug) => `${API_URL}/api/directory/drivers/${slug}/photo`;
export const directoryGalleryPhotoUrl = (slug, photoId) => `${API_URL}/api/directory/drivers/${slug}/gallery/${photoId}`;
export const getDirectoryProfile = () => api.get('/driver/directory-profile');
export const updateDirectoryProfile = (payload) => api.put('/driver/directory-profile', payload);
export const listDirectoryServices = () => api.get('/directory/services');
export const submitDirectoryForApproval = () => api.post('/driver/directory-profile/submit');
// Admin
export const adminListDirectoryApprovals = (params = {}) => api.get('/admin/directory-approvals', { params });
export const adminGetDirectoryApproval = (id) => api.get(`/admin/directory-approvals/${id}`);
export const adminApproveDirectory = (id) => api.post(`/admin/directory-approvals/${id}/approve`);
export const adminRejectDirectory = (id, reason = '') => api.post(`/admin/directory-approvals/${id}/reject`, { reason });
export const adminSuspendDirectory = (id, reason = '') => api.post(`/admin/directory-approvals/${id}/suspend`, { reason });
// Admin-only image URLs bypass the public visibility gate so a reviewing
// admin can see photos of a first-time PENDING driver before approval.
export const adminDirectoryPhotoUrl = (id) => `${API_URL}/api/admin/directory-approvals/${id}/photo`;
export const adminDirectoryHeaderUrl = (id) => `${API_URL}/api/admin/directory-approvals/${id}/header`;
export const adminDirectoryGalleryUrl = (id, photoId) => `${API_URL}/api/admin/directory-approvals/${id}/gallery/${photoId}`;
export const listMyDirectoryPhotos = () => api.get('/driver/directory-photos');
export const uploadDirectoryPhoto = (dataUrl) => api.post('/driver/directory-photos', { data: dataUrl });
export const deleteDirectoryPhoto = (photoId) => api.delete(`/driver/directory-photos/${photoId}`);
export const listDirectoryEnquiries = (params = {}) => api.get('/driver/directory-enquiries', { params });
export const markDirectoryEnquiryRead = (id, read = true) => api.post(`/driver/directory-enquiries/${id}/read${read ? '' : '?read=false'}`);
export const markAllDirectoryEnquiriesRead = () => api.post('/driver/directory-enquiries/read-all');
export const deleteDirectoryEnquiry = (id) => api.delete(`/driver/directory-enquiries/${id}`);
export const uploadDirectoryHeader = (dataUrl) => api.post('/driver/directory-branding/header', { data: dataUrl });
export const deleteDirectoryHeader = () => api.delete('/driver/directory-branding/header');
export const directoryHeaderUrl = (slug) => `${API_URL}/api/directory/drivers/${slug}/header`;
export const uploadDirectoryProfilePhoto = (formData) => api.post('/driver/directory-profile-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteDirectoryProfilePhoto = () => api.delete('/driver/directory-profile-photo');

// ============ Quick Post (guest, no-auth) ============
// Create a guest delivery without an account. Returns { id, guest_token, status, message }.
export const quickPostCreate = (payload) => api.post('/quick-post/deliveries', payload);
// Fetch the guest-side status page (delivery + offers).
export const quickPostStatus = (guestToken) => api.get(`/quick-post/status/${guestToken}`);
// Re-send the verification email.
export const quickPostResend = (guestToken) => api.post(`/quick-post/status/${guestToken}/resend-verification`);
// Detect if an email already has an account (for claim flow).
export const quickPostCheckEmail = (email) => api.get('/quick-post/check-email', { params: { email } });
// Link a verified guest delivery to the currently logged-in customer.
export const quickPostClaim = (guestToken) => api.post('/quick-post/claim', { guest_token: guestToken });

export default api;
